import React, { useCallback, useMemo, useState } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    Counterparty,
    FEDERAL_TAX_CLASSIFICATION_OPTIONS,
    FederalTaxClassification,
    getFederalTaxClassificationLabel,
} from "@common/types/counterparty";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Picker } from "@design-system/Picker/Picker.lib";
import { CounterpartyDirectoryViewRow } from "../counterpartyDirectoryViewTypes";
import { useIsTableReadonly } from "@hooks/useIsReadonlyOrLocked";
import { useUpdateCounterpartyFieldMutation } from "../useUpdateCounterpartyFieldMutation";

interface CounterpartyFederalTaxClassificationCellInnerProps {
    counterparty: Counterparty;
}

const CounterpartyFederalTaxClassificationCellInner: React.FC<CounterpartyFederalTaxClassificationCellInnerProps> =
    React.memo(({ counterparty }) => {
        const { updateField, isLoading } = useUpdateCounterpartyFieldMutation({
            counterparty,
            errorMessage: "Failed to update federal tax classification",
        });

        const classification = counterparty.federalTaxClassification ?? null;
        const [query, setQuery] = useState("");

        const visibleOptions = useMemo(() => {
            const normalizedQuery = query.trim().toLowerCase();
            return FEDERAL_TAX_CLASSIFICATION_OPTIONS.filter((option) =>
                option.label.toLowerCase().includes(normalizedQuery),
            );
        }, [query]);

        const handleValueChange = useCallback(
            (nextClassification: FederalTaxClassification | null) => {
                if (nextClassification === classification) {
                    return;
                }

                void updateField({
                    federalTaxClassification: nextClassification,
                });
            },
            [classification, updateField],
        );

        return (
            <ViewTableCell captureClicks>
                <Picker.Select
                    searchable
                    value={classification}
                    onValueChange={handleValueChange}
                    disabled={isLoading}
                    inputValue={query}
                    onInputValueChange={setQuery}
                    onOpenChange={(isOpen) => {
                        if (!isOpen) {
                            setQuery("");
                        }
                    }}
                >
                    <Picker.CellContent
                        value={
                            classification
                                ? getFederalTaxClassificationLabel(
                                      classification,
                                  )
                                : undefined
                        }
                        addText="Set classification"
                        hideAddIcon
                        readOnly={isLoading}
                    />
                    <Picker.Popup>
                        <Picker.Search placeholder="Search classifications..." />
                        <Picker.List>
                            {visibleOptions.length === 0 && (
                                <Picker.Empty>
                                    No matching classifications
                                </Picker.Empty>
                            )}
                            {visibleOptions.map((option) =>
                                option.value === null ? (
                                    <Picker.Item
                                        key="not-specified"
                                        onSelect={() => handleValueChange(null)}
                                    >
                                        {option.label}
                                    </Picker.Item>
                                ) : (
                                    <Picker.Item
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </Picker.Item>
                                ),
                            )}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </ViewTableCell>
        );
    });

CounterpartyFederalTaxClassificationCellInner.displayName =
    "CounterpartyFederalTaxClassificationCellInner";

export const CounterpartyFederalTaxClassificationCell: React.FC<
    CellContext<ViewTableFeatures, CounterpartyDirectoryViewRow, unknown>
> = React.memo(({ row, table }) => {
    const isReadonly = useIsTableReadonly(table);
    const { counterparty, isAccountRow, isNoCounterparty } = row.original;

    if (isAccountRow || isNoCounterparty || !counterparty) {
        return <ViewTableCell />;
    }

    if (isReadonly) {
        return (
            <ViewTableCell>
                {counterparty.federalTaxClassification
                    ? getFederalTaxClassificationLabel(
                          counterparty.federalTaxClassification,
                      )
                    : null}
            </ViewTableCell>
        );
    }

    return (
        <CounterpartyFederalTaxClassificationCellInner
            counterparty={counterparty}
        />
    );
});

CounterpartyFederalTaxClassificationCell.displayName =
    "CounterpartyFederalTaxClassificationCell";
